import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { PagesResponse } from "~/api/Api";
import { queryKeys } from "~/api/queryKeys";
import type { ConditionGroup } from "~/components/dashboard/PageTable/AdvancedConditionsModal";
import { GenerateCustomizationsModal } from "~/components/dashboard/PageTable/GenerateCustomizationsModal";
import Button from "~/components/mantine/button/Button.tsx";
import { AlertCard } from "~/components/ui/card/AlertCard";
import { PaginationConfig } from "~/components/ui/pagination/Pagination";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { SiteCableContext } from "~/contexts/SiteCableContext";
import { useTeam } from "~/contexts/TeamProvider";
import { canCollection, extractAuthorizations } from "~/helpers/authorizations";
import { buildSearchConditions } from "~/helpers/buildPageSearchConditions";
import { CRAWL_MODE } from "~/helpers/consts";
import { useSitePermissions } from "~/hooks/permissions/useSitePermissions.ts";
import { usePageTitle } from "~/hooks/usePageTitle";

import {
  PageBulkRecrawlAttributes,
  PageStatusStats,
  PageTypeStats,
} from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import PageTable from "../../components/dashboard/PageTable/PageTable";
import NotificationBlock from "../../components/notificationBlock/NotificationBlock";
import { useSiteContext } from "../../contexts/SiteContext";
import { pluralize } from "../../helpers/helpers";
import styles from "./styles.module.scss";

type PageAuthorization = "recrawl" | "toggle_ignore";
type PagesAuthorization = "bulk_recrawl" | "bulk_ignore" | "create" | "crawl";

type PagesAuthorizations = {
  collection: PagesAuthorization[];
  member: PageAuthorization[][];
};

type PaginationMeta = {
  totalItems: number;
  nextPage: number;
  prevPage: number;
  totalPages: number;
};

export type PagesResponseWithAuth = PagesResponse & {
  authorizations?: PagesAuthorizations;
  paginationMeta?: PaginationMeta;
};

export interface StatusObject {
  value: string;
  label: string;
}

const FILTER_VALUES = {
  ALL: "all",
  DIVIDER: "divider",
};
export const DEFAULT_STATUSES = [
  "pending",
  "in_progress",
  "failed",
  "completed",
];

type IgnoreAction = "ignore" | "include";

export interface IgnoreRecrawlPayload {
  id: number;
  action: IgnoreAction;
}

const filterZeroValues = (
  stats: Record<string, number | string | null | undefined>,
): Record<string, number> => {
  return Object.entries(stats || {}).reduce(
    (acc, [key, value]) => {
      if (Number(value) > 0) {
        acc[key] = Number(value);
      }

      return acc;
    },
    {} as Record<string, number>,
  );
};

const PagesPage = () => {
  usePageTitle(PAGE_TITLES.pages);
  const { id: siteId } = useParams();

  if (!siteId) {
    throw new Error("No site id provided");
  }

  const { team } = useTeam();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>("");
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [generateSelectedIds, setGenerateSelectedIds] = useState<number[]>([]);
  const [advancedConditions, setAdvancedConditions] = useState<
    ConditionGroup[]
  >([]);
  const [sorting, setSorting] = useState<string[]>([]);
  const [urlServerError, setUrlServerError] = useState<string | null>(null);
  const refetchOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.pagesRoot(siteId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.siteDetails(siteId) });
    queryClient.invalidateQueries({
      queryKey: queryKeys.pageTypesDetailRoot(siteId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.pageStatusesDetailRoot(siteId),
    });
  };

  const location = useLocation();
  const { selectedPageType } = location.state || {};

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    selectedPageType ? ["completed"] : DEFAULT_STATUSES,
  );
  const [selectedPageTypes, setSelectedPageTypes] = useState<string[]>(
    selectedPageType ? [selectedPageType] : ["all"],
  );
  const [selectedCustomizations, setSelectedCustomizations] = useState<
    string[]
  >(["all"]);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });
  const { data: pageStatusesDetail } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: queryKeys.pageStatusesDetail(siteId, selectedPageTypes),
    queryFn: async () => {
      const response = await apiClient.sites.pagesStatusesDetail(
        Number(siteId),
        {
          ...(!selectedPageTypes.includes(FILTER_VALUES.ALL) && {
            q: {
              // @ts-expect-error BE types - missing input field
              kind_in: selectedPageTypes,
            },
          }),
        },
      );

      const statuses: Partial<PageStatusStats> =
        response?.data?.page_status_stats || {};

      // Filter out zero values except those in DEFAULT_STATUSES.
      const validStatusesObject = Object.fromEntries(
        Object.entries(statuses).filter(
          ([key, value]) => DEFAULT_STATUSES.includes(key) || value > 0,
        ),
      );

      // Ensure all default statuses are present (add them with a count of 0 if missing)
      DEFAULT_STATUSES.forEach((defaultStatus) => {
        if (!(defaultStatus in validStatusesObject)) {
          validStatusesObject[defaultStatus] = 0;
        }
      });

      return validStatusesObject;
    },
  });

  const { data: pageTypesDetail } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: queryKeys.pageTypesDetail(siteId, selectedStatuses),
    queryFn: async () => {
      // we need to pass the selected statuses to the API to get the correct page types
      const resp = await apiClient.sites.pagesTypesDetail(Number(siteId), {
        ...(!selectedStatuses.includes(FILTER_VALUES.ALL) && {
          q: {
            // @ts-expect-error BE types - missing input field
            status_in: selectedStatuses,
          },
        }),
      });
      const types: Partial<PageTypeStats> = resp?.data?.page_type_stats || {};

      return filterZeroValues(types);
    },
  });

  const searchConditions = buildSearchConditions({
    selectedStatuses,
    selectedPageTypes,
    selectedCustomizations,
    searchValue,
    advancedConditions,
  });

  const pageQueryKeyParams = {
    pageSize: pagination.itemsPerPage,
    pageNumber: pagination.currentPage,
    sorting,
    searchValue,
    selectedStatuses,
    selectedPageTypes,
    selectedCustomizations,
    advancedConditions,
  };

  const {
    data: pagesData,
    isLoading: isPagesLoading,
    isFetching: isPagesFetching,
  } = useQuery<PagesResponseWithAuth>({
    // Keep previous rows visible during filter / pagination / sort refetches
    // so the table doesn't blank-and-jump. Skeleton is shown only on the very
    // first load (when there is no data yet).
    placeholderData: keepPreviousData,
    queryKey: queryKeys.pagesList(siteId, pageQueryKeyParams),
    queryFn: async () => {
      const response = await apiClient.sites.pagesDetail(
        Number(siteId),
        {
          q: searchConditions,
          "page[size]": pagination.itemsPerPage,
          "page[number]": pagination.currentPage,
          ...(sorting.length > 0 && { s: sorting.join(", ") }),
        },
        {
          headers: {
            "Authorize-Member-Actions": "recrawl,toggle_ignore",
            "Authorize-Collection-Actions":
              "bulk_recrawl,bulk_ignore,create,crawl",
          },
        },
      );
      const { headers } = response;

      return {
        ...response.data,
        paginationMeta: {
          totalItems: Number(headers.get("x-pager-total-count")),
          nextPage: Number(headers.get("x-pager-next-page")),
          prevPage: Number(headers.get("x-pager-prev-page")),
          totalPages: Number(headers.get("x-pager-total-pages")),
        },
      };
    },
  });

  // Show skeleton only on the initial load — keepPreviousData makes filter /
  // pagination refetches surface as isFetching, not isLoading.

  useEffect(() => {
    if (!pagesData?.paginationMeta) return;

    setPagination((prev) => ({ ...prev, ...pagesData.paginationMeta }));
  }, [pagesData?.paginationMeta]);

  // Lightweight query to check authorization for creating customization actions
  const { data: customizationAuth } = useQuery({
    queryKey: queryKeys.siteCustomizationActions(siteId),
    queryFn: async () => {
      const response = await apiClient.sites.siteCustomizationActionsDetail(
        Number(siteId),
        { "page[size]": 1 },
        {
          headers: {
            "Authorize-Collection-Actions": "create",
          },
        },
      );

      return extractAuthorizations(response.data);
    },
    enabled: !!team?.ai_engine_available,
  });

  const hasCompletedInScope =
    selectedStatuses.includes("all") || selectedStatuses.includes("completed");

  const canGenerateCustomizations =
    !!team?.ai_engine_available &&
    canCollection(customizationAuth, "create") &&
    hasCompletedInScope;

  // toggles between ignored/pending statuses on backend
  const { mutate: ignoreRecrawlPage } = useMutation<
    void,
    unknown,
    IgnoreRecrawlPayload
  >({
    mutationFn: async ({ id }) => {
      await apiClient.sites.pagesToggleIgnoreCreate(Number(siteId), id);
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
      queryClient.invalidateQueries({ queryKey: ["pages"] });
    },
    onSuccess: (_data, variables) => {
      const text =
        variables.action === "ignore"
          ? "Page was successfully ignored for crawling"
          : "Page was successfully included in crawling";

      enqueueSnackbar(text, {
        variant: "success",
      });
      refetchOnSuccess();
    },
  });

  const { mutate: forceRecrawlPage } = useMutation({
    mutationFn: async () => {
      await apiClient.sites.recrawlCreate(Number(siteId));
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar(
        "Site was successfully added to queue and will be recrawled soon",
        {
          variant: "success",
        },
      );
      // only refetch site to update crawl_status — pages/types/statuses
      // haven't changed yet, they'll be refreshed when crawling finishes
      queryClient.invalidateQueries({
        queryKey: queryKeys.siteDetails(siteId),
      });
    },
  });

  const { mutate: softCrawlPage, isPending: isSoftCrawlPagePending } =
    useMutation({
      mutationFn: async () => {
        await apiClient.sites.crawlCreate(Number(siteId));
      },
      onError: (error) => {
        // @ts-expect-error from the response
        const errorMessage = error?.error?.error?.message || "Error";
        enqueueSnackbar(errorMessage, {
          variant: "error",
          persist: true,
        });
      },
      onSuccess: () => {
        enqueueSnackbar(
          "Site was successfully added to queue and will be recrawled soon",
          {
            variant: "success",
          },
        );
        queryClient.invalidateQueries({
          queryKey: queryKeys.siteDetails(siteId),
        });
      },
    });

  const { mutate: addUrl } = useMutation({
    mutationFn: async (url: string) => {
      await apiClient.sites.pagesCreate(Number(siteId), { url });
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      setUrlServerError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      setUrlServerError(null);
      enqueueSnackbar("Page was successfully added", {
        variant: "success",
      });
      refetchOnSuccess();
    },
  });

  const { mutate: recrawlPagesBulk } = useMutation({
    mutationFn: async (data: PageBulkRecrawlAttributes) => {
      await apiClient.sites.pagesBulkRecrawlCreate(Number(siteId), data);
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Pages were successfully recrawled", {
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.siteDetails(siteId),
      });
    },
  });

  const { mutate: ignorePagesBulk } = useMutation({
    mutationFn: async (data: PageBulkRecrawlAttributes) => {
      await apiClient.sites.pagesBulkIgnoreCreate(Number(siteId), data);
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Pages were successfully ignored", {
        variant: "success",
      });
      refetchOnSuccess();
    },
  });

  const { mutate: recrawlPage } = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.sites.pagesRecrawlCreate(Number(siteId), id);
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar(
        "The pages were successfully added to queue and will be recrawled soon",
        {
          variant: "success",
        },
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.siteDetails(siteId),
      });
    },
  });

  const { queuedCount } = useContext(SiteCableContext);
  const siteContext = useSiteContext();
  const site = siteContext?.site?.site;
  const crawledAt = site?.crawled_at;
  const crawlStatus = site?.crawl_status || "";
  const isCrawlQueued = site?.crawl_queued;
  const sitePermissions = useSitePermissions(siteId);
  const canSoftCrawl = sitePermissions.canSoftCrawl;

  // only before the 1st crawl per site
  const isShowPageImportAlert =
    site &&
    // @ts-expect-error TODO: remove after site.page_import API schema would be fixed and would exclude String type
    site.page_import?.status === "completed" &&
    site.crawl_mode === CRAWL_MODE.WHITELISTED &&
    canSoftCrawl &&
    site.crawl_status === "unknown";

  let status, crawledAtTs;

  switch (crawlStatus) {
    case "unknown":
      status = "queued";
      break;
    case "in_progress":
      status = "in progress...";
      break;
    case "finished":
      status = "finalizing...";
      break;
    case "analysing":
      status = "crawl complete, optimizing...";
      break;
    case "aborted":
      status = "aborting...";
      break;
    default:
      // failed/completed
      status = isCrawlQueued ? "queued" : crawlStatus?.split("_")?.join(" ");

      if (crawledAt) {
        crawledAtTs = new Date(crawledAt);
      }
  }

  let crawlDetails: ReactNode = null;

  if (queuedCount && queuedCount > 0) {
    crawlDetails = (
      <span key="queue">
        {queuedCount} {pluralize("page", queuedCount)} in the queue
      </span>
    );
  } else if (crawledAtTs) {
    crawlDetails = (
      <em key="crawled">
        last crawled{" "}
        <time dateTime={crawledAtTs.toISOString()}>
          {formatDistanceToNow(crawledAtTs, { addSuffix: true })}
        </time>
      </em>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.pages}</h1>
      {site?.crawl_mode === CRAWL_MODE.WHITELISTED && !site?.page_import && (
        <div className={styles.notification}>
          <NotificationBlock
            type="info"
            message="Whitelist crawl mode is enabled, but the import file is not included. New pages won't be discovered and crawled."
          ></NotificationBlock>
        </div>
      )}
      {isShowPageImportAlert && (
        <div className={styles.notification}>
          <AlertCard
            title="Page Import completed"
            type="neutral"
            content={
              <>
                <div>
                  Your pages are ready! Double-check everything looks good, then
                  start your first crawl.
                </div>
              </>
            }
            actions={
              <>
                <Button
                  disabled={isSoftCrawlPagePending}
                  onClick={() => softCrawlPage()}
                >
                  Crawl Now
                  {isSoftCrawlPagePending && <Spinner size="1rem" />}
                </Button>
              </>
            }
          />
        </div>
      )}
      <div className={styles.crawlStats}>
        <p>
          Status: {status}
          {crawlDetails && (
            <span className={styles.mutedText}> ({crawlDetails})</span>
          )}
        </p>
      </div>
      <PageTable
        pageTypesDetail={pageTypesDetail || {}}
        pageStatusesDetail={pageStatusesDetail || {}}
        setSelectedStatuses={setSelectedStatuses}
        setSelectedPageTypes={setSelectedPageTypes}
        selectedPageTypes={selectedPageTypes}
        selectedCustomizations={selectedCustomizations}
        setSelectedCustomizations={setSelectedCustomizations}
        selectedStatuses={selectedStatuses}
        data={pagesData}
        pagination={pagination}
        isLoading={isPagesLoading}
        isFetching={isPagesFetching}
        setPagination={setPagination}
        recrawlPage={recrawlPage}
        ignoreRecrawlPage={ignoreRecrawlPage}
        recrawlPagesBulk={recrawlPagesBulk}
        forceRecrawlPage={forceRecrawlPage}
        softCrawlPage={softCrawlPage}
        ignorePagesBulk={ignorePagesBulk}
        createPageUrl={addUrl}
        urlServerError={urlServerError}
        setUrlServerError={setUrlServerError}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        sorting={sorting}
        setSorting={setSorting}
        canGenerate={canGenerateCustomizations}
        onGenerateClick={(selectedIds) => {
          setGenerateSelectedIds(selectedIds);
          setIsGenerateModalOpen(true);
        }}
        advancedConditions={advancedConditions}
        onAdvancedConditionsChange={setAdvancedConditions}
      />
      <GenerateCustomizationsModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        siteId={Number(siteId)}
        selectedStatuses={selectedStatuses}
        selectedPageTypes={selectedPageTypes}
        selectedCustomizations={selectedCustomizations}
        searchValue={searchValue}
        advancedConditions={advancedConditions}
        selectedIds={generateSelectedIds}
      />
    </div>
  );
};
export default PagesPage;
